import {
  Component,
  Input,
  Output,
  EventEmitter,
  PLATFORM_ID,
  Optional,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  LucideAngularModule,
  ArrowLeft,
  ScanLine,
  Car,
  FileText,
  CheckCircle,
} from 'lucide-angular';
import { VinScannerModal } from '../vin-scanner-modal/vin-scanner-modal';
import { EmailValidationService } from '../../../services/email-validation.service';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';

// Importar la función de detección de dispositivos
import { isMobileOrTablet } from '../../../utils/stores/store-data';
import {
  AnioVehiculoResponse,
  Auto,
  MarcaVehiculoResponse,
  ModeloVehiculoResponse,
  MotorVehiculoResponse,
  SubmodeloVehiculoResponse,
} from '../../../types/vehicle.types';
import { VehicleService } from '../../../services/vehicle.service';
import {
  LOCAL_STORAGE_KEY_AUTH_TOKEN,
  LOCAL_STORAGE_KEY_USER_DATA,
} from '../../../config/variables';
import { DialogModule } from 'primeng/dialog';
import { NotificationService } from '../../../services/notification.service';
import { NavigationService } from '../../../services/navigation.service';

export type SearchMethod = 'select' | 'manual' | 'vin' | 'plate';

@Component({
  selector: 'allas-add-vehicle-form',
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    VinScannerModal,
    DialogModule,
  ],
  templateUrl: './add-vehicle-form.html',
  providers: [DialogService],
})
export class AddVehicleForm {
  @Input() onVehicleAdded?: () => void;
  @Input() onCancel?: () => void;
  @Input() isModal: boolean = false;

  @Output() vehicleAdded = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  // Servicios inyectados lazy
  private vehicleService = inject(VehicleService);
  private emailValidationService = inject(EmailValidationService);
  private notificationService = inject(NotificationService);
  private navigationService = inject(NavigationService);
  private platformId = inject(PLATFORM_ID);

  readonly arrowLeftIcon = ArrowLeft;
  readonly scanLineIcon = ScanLine;
  readonly carIcon = Car;
  readonly fileTextIcon = FileText;
  readonly checkCircleIcon = CheckCircle;
  readonly checkIcon = CheckCircle;

  searchMethod: SearchMethod = 'select';
  isVinScannerOpen: boolean = false;
  vinInput: string = '';
  isSearchingVin: boolean = false;
  vinSearchComplete: boolean = false;
  showVinGuide: boolean = false;
  isUserLoggedIn: boolean = false;
  isSubmitting: boolean = false;

  formData: Auto = {
    anio: 0,
    marca: '',
    modelo: '',
    motor: '',
    alias: '',
    idSubmodeloMotor: null,
    vehicleEngineConfig: null,
    predeterminado: false,
    idUsuarioDigital: 0,
    id: 0,
    vin: '',
    placa: null,
    fechaCreacion: '',
    activo: true,
  };

  years: number[] = [];
  motoresDisponibles: string[] = [];
  totalSteps: number = 7;
  finalStep: number = 7;

  // Nuevos

  actualStep = 1;
  stepTitle = 'Selecciona el año';
  steps = [
    {
      step: 1,
      title: 'Selecciona el año',
      description: 'Selecciona el año de tu vehículo',
    },
    {
      step: 2,
      title: 'Selecciona la marca',
      description: 'Selecciona la marca de tu vehículo',
    },
    {
      step: 3,
      title: 'Selecciona el modelo',
      description: 'Selecciona el modelo de tu vehículo',
    },
    {
      step: 4,
      title: 'Selecciona el submodelo',
      description: 'Selecciona el submodelo de tu vehículo',
    },
    {
      step: 5,
      title: 'Selecciona el motor',
      description: 'Selecciona el motor de tu vehículo',
    },
    {
      step: 6,
      title: 'Personaliza tu vehículo',
      description: 'Personaliza tu vehículo',
    },
    {
      step: 7,
      title: 'Confirma los datos del vehículo',
      description: 'Confirma los datos del vehículo',
    },
  ];

  loadingRequest = false;

  anios: AnioVehiculoResponse[] = [];
  anioSelected: AnioVehiculoResponse | null = null;

  marcas: MarcaVehiculoResponse[] = [];
  marcaSelected: MarcaVehiculoResponse | null = null;

  modelos: ModeloVehiculoResponse[] = [];
  modeloSelected: ModeloVehiculoResponse | null = null;

  submodelos: SubmodeloVehiculoResponse[] = [];
  submodeloSelected: SubmodeloVehiculoResponse | null = null;

  motores: MotorVehiculoResponse[] = [];
  motorSelected: MotorVehiculoResponse | null = null;

  handleClickOnYear(year: AnioVehiculoResponse) {
    this.anioSelected = year;
    this.formData.anio = this.anioSelected.anio;
    this.getMarcas();
  }

  getAnios() {
    this.changeLoadingRequest(true);
    this.vehicleService.getAnios().subscribe({
      next: (anios) => {
        this.anios = anios;
      },
      error: (error) => {
        console.error('Error obteniendo años:', error);
      },
      complete: () => {
        this.changeLoadingRequest(false);
      },
    });
  }

  handleClickOnMarca(marca: MarcaVehiculoResponse) {
    this.marcaSelected = marca;
    this.formData.marca = this.marcaSelected.descripcion;
    this.getModelos();
  }

  getMarcas() {
    if (!this.anioSelected) return;

    this.changeLoadingRequest(true);

    this.vehicleService.getMarcas(this.anioSelected.anio).subscribe({
      next: (marcas) => {
        this.marcas = marcas;
        // Usar setTimeout para evitar ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          this.nextStep();
        }, 0);
      },
      error: (error) => {
        console.error('Error obteniendo marcas:', error);
        this.changeLoadingRequest(false);
      },
      complete: () => {
        console.log('Marcas obtenidas');
        this.changeLoadingRequest(false);
      },
    });
  }

  changeLoadingRequest(loading: boolean) {
    setTimeout(() => {
      this.loadingRequest = loading;
      this.cdr.detectChanges();
    }, 0);
    // this.loadingRequest = loading;
    // this.cdr.detectChanges();
  }

  handleClickOnModelo(modelo: ModeloVehiculoResponse) {
    this.modeloSelected = modelo;
    this.formData.modelo = this.modeloSelected.descripcion;
    this.getSubmodelos();
  }

  getModelos() {
    if (!this.marcaSelected) return;
    if (!this.anioSelected) return;

    this.formData.marca = this.marcaSelected.descripcion;

    this.changeLoadingRequest(true);

    this.vehicleService
      .getModelos(this.anioSelected.anio, this.marcaSelected.id_marca)
      .subscribe({
        next: (modelos) => {
          this.modelos = modelos;
          // Usar setTimeout para evitar ExpressionChangedAfterItHasBeenCheckedError
          setTimeout(() => {
            this.nextStep();
          }, 0);
        },
        error: (error) => {
          console.error('Error obteniendo modelos:', error);
          this.changeLoadingRequest(false);
        },
        complete: () => {
          this.changeLoadingRequest(false);
        },
      });
  }

  handleClickOnSubmodelo(submodelo: SubmodeloVehiculoResponse) {
    this.submodeloSelected = submodelo;
    this.formData.idSubmodeloMotor = this.submodeloSelected.id_modelo_anio;
    this.getMotores();
  }

  getSubmodelos() {
    if (!this.anioSelected) return;
    if (!this.marcaSelected) return;
    if (!this.modeloSelected) return;

    this.formData.modelo = this.modeloSelected.descripcion;

    this.changeLoadingRequest(true);

    this.vehicleService
      .getSubmodelos(this.modeloSelected.id_modelo_anio)
      .subscribe({
        next: (submodelos) => {
          this.submodelos = submodelos;
          // Usar setTimeout para evitar ExpressionChangedAfterItHasBeenCheckedError
          setTimeout(() => {
            this.nextStep();
          }, 0);
        },
        error: (error) => {
          console.error('Error obteniendo submodelos:', error);
          this.changeLoadingRequest(false);
        },
        complete: () => {
          this.changeLoadingRequest(false);
        },
      });
  }

  handleClickOnMotor(motor: MotorVehiculoResponse) {
    console.log('Motor seleccionado:', motor);
    this.motorSelected = motor;
    this.formData.idSubmodeloMotor = motor.id_submodelo_motor;
    this.formData.motor = this.motorSelected.descripcion;
    this.nextStep();
  }

  getMotores() {
    if (!this.anioSelected) return;
    if (!this.marcaSelected) return;
    if (!this.modeloSelected) return;
    if (!this.submodeloSelected) return;

    // this.formData.idSubmodeloMotor = this.motorSelected?.id_submodelo_motor || null;

    this.changeLoadingRequest(true);

    this.vehicleService
      .getMotores(
        this.modeloSelected.id_modelo_anio,
        this.submodeloSelected.descripcion
      )
      .subscribe({
        next: (motores) => {
          this.motores = motores;
          // Usar setTimeout para evitar ExpressionChangedAfterItHasBeenCheckedError
          setTimeout(() => {
            this.nextStep();
          }, 0);
        },
        error: (error) => {
          console.error('Error obteniendo motores:', error);
          this.changeLoadingRequest(false);
        },
        complete: () => {
          this.changeLoadingRequest(false);
        },
      });
  }

  // METODOS DE LOS PASOS
  setStep(step: number) {
    const pasoExistente = this.steps.find((s) => s.step === step);

    if (!pasoExistente) return;

    this.actualStep = pasoExistente.step;

    this.setStepTitle();

    this.cdr.detectChanges();
  }

  nextStep() {
    if (this.actualStep === this.finalStep) return;

    this.setStep(this.actualStep + 1);
  }

  previousStep() {
    if (this.actualStep === 1) return;

    this.setStep(this.actualStep - 1);
  }

  setStepTitle() {
    const titles = this.steps.find((s) => s.step === this.actualStep);

    this.stepTitle = titles?.title || '';
  }

  // FIN METODOS DE LOS PASOS

  // LIFE CYCLE METHODS

  ngOnInit() {
    this.cdr.detectChanges();
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.cdr.detach();
  }

  // FIN LIFE CYCLE METHODS

  // CONSTRUCTOR
  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    @Optional() private dialogRef: DynamicDialogRef,
    @Optional() private dialogConfig: DynamicDialogConfig
  ) {
    // Inyección lazy de servicios para evitar problemas con modales
    this.getAnios();

    // Usar platformId del modal si está disponible, sino usar el inyectado
    const modalPlatformId = this.dialogConfig?.data?.platformId;
    if (modalPlatformId) {
      this.platformId = modalPlatformId;
    }

    if (isPlatformBrowser(this.platformId)) {
      if (this.dialogConfig?.data?.isModal !== undefined) {
        this.isModal = this.dialogConfig.data.isModal;
        this.onVehicleAdded = this.dialogConfig.data.onVehicleAdded;
        this.onCancel = this.dialogConfig.data.onCancel;
      }
    }

    // Asegurar que searchMethod esté en 'select' para mostrar las opciones
    this.searchMethod = 'select';
    this.vinSearchComplete = false;

    // Verificar estado de autenticación
    this.checkAuthenticationStatus();
  }

  /**
   * Verifica el estado de autenticación del usuario
   */
  private checkAuthenticationStatus(): void {
    if (!this.isBrowser()) {
      return;
    }

    const token = localStorage.getItem(LOCAL_STORAGE_KEY_AUTH_TOKEN);
    const userData = localStorage.getItem(LOCAL_STORAGE_KEY_USER_DATA);

    this.isUserLoggedIn = !!(token && userData);
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  handleBack(): void {
    switch (this.searchMethod) {
      case 'select':
        this.handleSelectMethodBack();
        break;
      case 'manual':
        this.handleManualMethodBack();
        break;
      case 'vin':
        this.handleVinMethodBack();
        break;
      default:
        this.resetToSelection();
    }
  }

  private handleSelectMethodBack(): void {
    if (this.isModal) {
      this.closeModal();
    } else {
      this.navigateToGarage();
    }
  }

  private handleManualMethodBack(): void {
    if (this.actualStep > 1) {
      this.previousStep();
    } else {
      this.resetToSelection();
    }
  }

  private handleVinMethodBack(): void {
    if (this.vinSearchComplete) {
      this.resetVinSearchState();
    } else {
      this.resetToSelection();
    }
  }

  private closeModal(): void {
    if (this.onCancel) {
      this.onCancel();
    } else {
      this.cancelled.emit();
      this.dialogRef?.close();
    }
  }

  private navigateToGarage(): void {
    this.router.navigate(['/garage']);
  }

  private resetVinSearchState(): void {
    this.vinSearchComplete = false;
    this.vinInput = '';
    this.resetFormData();
  }

  private resetToSelection(): void {
    this.searchMethod = 'select';
    this.setStep(1);
    this.vinSearchComplete = false;
    this.vinInput = '';
  }

  private resetFormData(): void {
    this.formData = {
      anio: 0,
      marca: '',
      modelo: '',
      motor: '',
      alias: '',
      idSubmodeloMotor: null,
      predeterminado: false,
      idUsuarioDigital: 0,
      vehicleEngineConfig: null,
      id: 0,
      vin: '',
      placa: null,
      fechaCreacion: '',
      activo: true,
    };
  }

  async handleVinSearch(): Promise<void> {
    if (this.vinInput.length !== 17) return;

    this.isSearchingVin = true;

    try {
      const vehicleData = await this.vehicleService.decodeVin(this.vinInput);
      if (vehicleData) {
        this.formData = {
          motor: vehicleData.motor,
          marca: vehicleData.marca,
          modelo: vehicleData.modelo,
          anio: vehicleData.anio,
          alias: vehicleData.alias,
          idSubmodeloMotor: vehicleData.idSubmodeloMotor,
          predeterminado: false,
          idUsuarioDigital: 0,
          vehicleEngineConfig: vehicleData.vehicleEngineConfig,
          id: 0,
          vin: vehicleData.vin || '',
          placa: null,
          fechaCreacion: new Date().toISOString(),
          activo: true,
        };
        this.vinSearchComplete = true;
      }
    } catch (error) {
      console.error('Error searching VIN:', error);
    } finally {
      this.isSearchingVin = false;
    }
  }

  handleVinFound(vehicleData: Auto): void {
    this.formData = {
      motor: vehicleData.motor,
      marca: vehicleData.marca,
      modelo: vehicleData.modelo,
      anio: vehicleData.anio,
      alias: vehicleData.alias,
      vin: vehicleData.vin || '',
      placa: null,
      fechaCreacion: new Date().toISOString(),
      activo: true,
      idSubmodeloMotor: vehicleData.idSubmodeloMotor,
      vehicleEngineConfig: vehicleData.vehicleEngineConfig,
      predeterminado: false,
      idUsuarioDigital: 0,
      id: 0,
    };

    // Guardar el VIN del vehículo encontrado
    this.vinInput = vehicleData.vin || 'VIN no disponible';
    this.isVinScannerOpen = false;
    this.vinSearchComplete = true;
  }

  async handleSubmit(): Promise<void> {
    if (this.isSubmitting) {
      return; // Evitar múltiples envíos
    }

    this.setIsSubmitting(true);

    const newVehicle: Auto = {
      id: 0,
      idUsuarioDigital: 0,
      predeterminado: false,
      alias: this.formData.alias,
      vin: this.formData.vin || '',
      placa: null,
      anio: this.formData.anio,
      marca: this.formData.marca,
      modelo: this.formData.modelo,
      motor: this.formData.motor,
      idSubmodeloMotor:
        this.formData.idSubmodeloMotor ||
        this.motorSelected?.id_submodelo_motor ||
        null,
      vehicleEngineConfig:
        this.formData.vehicleEngineConfig ||
        this.motorSelected?.vehicle_to_engine_config ||
        null,
      fechaCreacion: new Date().toISOString(),
      activo: true,
    };

    try {
      const idUsuarioDigital = this.isUserLoggedIn
        ? this.emailValidationService.getCurrentUser()?.idUsuarioDigital || 0
        : 0;

      newVehicle.idUsuarioDigital = idUsuarioDigital;

      await this.vehicleService.add({
        ...newVehicle,
        idUsuarioDigital: idUsuarioDigital,
      });

      this.notificationService.showSuccess(
        'Vehículo agregado correctamente, serás redirigido de vuelta a dónde estabas'
      );

      if (this.dialogRef) {
        this.dialogRef.close(true);
      } else {
        this.redirectToPreviousPreviosUrl();
      }
    } catch (error) {
      // Mostrar mensaje de error al usuario
      this.notificationService.showError(
        'Error al agregar el vehículo. Por favor, inténtalo de nuevo.'
      );

      console.error('Error al agregar el vehículo:', error);

      this.setIsSubmitting(false);
    } finally {
      this.setIsSubmitting(false);
    }
  }

  private setIsSubmitting(isSubmitting: boolean) {
    setTimeout(() => {
      this.isSubmitting = isSubmitting;
      this.cdr.detectChanges();
    }, 0);
  }

  private redirectToPreviousPreviosUrl(): void {
    this.navigationService.goBack(true);
  }

  // Method selection handlers
  selectManualMethod(): void {
    this.searchMethod = 'manual';
    this.setStep(1);
  }

  openVinScanner(): void {
    this.isVinScannerOpen = true;
  }

  closeVinScanner(): void {
    this.isVinScannerOpen = false;
  }

  continueToConfirmation(): void {
    this.setStep(7);
  }

  // VIN input handler
  onVinInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.vinInput = target.value.toUpperCase();
  }

  // Utility methods
  get progressPercentage(): number {
    const percentage = (this.actualStep / this.finalStep) * 100;
    if (percentage > 100) {
      return 100;
    }
    return percentage;
  }

  get shouldShowProgressBar(): boolean {
    return this.searchMethod !== 'select' && this.searchMethod !== 'vin';
  }

  get shouldHideHeader(): boolean {
    return this.vinSearchComplete;
  }

  getStepLabel(stepNumber: number): string {
    const labels: { [key: number]: string } = {
      1: 'Año',
      2: 'Marca',
      3: 'Modelo',
      4: 'Submodelo',
      5: 'Motor',
      6: 'Personalizar',
      7: 'Confirmar',
    };
    return labels[stepNumber] || '';
  }

  getStepNumbers(): number[] {
    return this.isUserLoggedIn ? [1, 2, 3, 4, 5, 6, 7] : [1, 2, 3, 4, 5, 7];
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    if (target) {
      target.src = '/placeholder.svg';
    }
  }
}
